import { ReactRenderer } from '@tiptap/react';
import { Editor } from '@tiptap/core';
import tippy, { Instance } from 'tippy.js';

import MentionList from './MentionList.tsx';
import { UserBase } from '@tickitacka/shared';

interface SuggestionOptions {
    userList: UserBase[];
}

interface MentionRenderProps {
    items: UserBase[];
    command: (props: { id: string; label: string }) => void;
    query: string;
    range: { from: number; to: number };
    editor: Editor;
    clientRect: () => DOMRect | null;
}

export default function createSuggestion({ userList }: SuggestionOptions) {
    return {
        items: ({ query }: { query: string }) => {
            const filteredUserList = userList.filter(user =>
                user.name.toLowerCase().startsWith(query.toLowerCase())
            );
            // .slice(0, 5);

            return filteredUserList.sort((a, b) =>
                a.name.localeCompare(b.name)
            );
        },

        render: () => {
            let component: ReactRenderer | null = null;
            let popup: Instance[] | null = null;
            let isDestroyed = false;

            return {
                onStart: (props: MentionRenderProps) => {
                    // 이전 인스턴스가 있다면 정리 (예: onExit 없이 onStart가 다시 호출되는 경우)
                    if (component) {
                        component.destroy();
                    }
                    if (popup?.[0]) {
                        popup[0].destroy();
                    }

                    isDestroyed = false; // 새 시작이므로 플래그 리셋

                    component = new ReactRenderer(MentionList, {
                        props,
                        editor: props.editor,
                    });

                    if (!props.clientRect) {
                        // clientRect가 없으면 tippy를 생성할 수 없음
                        popup = null;
                        return;
                    }

                    popup = tippy('body', {
                        getReferenceClientRect: props.clientRect,
                        appendTo: () => document.body,
                        content: component.element,
                        showOnCreate: true,
                        interactive: true,
                        trigger: 'manual',
                        placement: 'bottom-start',
                    });
                },

                onUpdate(props: MentionRenderProps) {
                    if (isDestroyed || !component) {
                        return;
                    }

                    component.updateProps(props);

                    if (!props.clientRect) {
                        return;
                    }

                    if (popup?.[0]) {
                        popup[0].setProps({
                            getReferenceClientRect: props.clientRect,
                        });
                    }
                },

                onKeyDown(props: { event: KeyboardEvent }) {
                    if (isDestroyed) {
                        return false;
                    }

                    if (props.event.key === 'Escape') {
                        if (popup?.[0]) {
                            popup[0].hide();
                        }
                        return true;
                    }
                    // component 및 component.ref와 onKeyDown 메소드가 존재하는지 확인
                    const componentRef = component?.ref as { onKeyDown?: (props: { event: KeyboardEvent }) => boolean };
                    return componentRef?.onKeyDown?.(props) ?? false;
                },

                onExit() {
                    if (isDestroyed) {
                        return;
                    }

                    if (popup?.[0]) {
                        popup[0].destroy();
                    }
                    if (component) {
                        component.destroy();
                    }

                    isDestroyed = true;
                    popup = null;
                    component = null;
                },
            };
        },
    };
}
